import { useLocation } from 'wouter-preact'
import { useCallback } from 'preact/hooks'
import { ProcessDtoLiteStatus } from '@cencert/mob-signature'
import { useNavigationGuard } from '@/services/navigation-guard'
import { advanceToStep } from '@/services/navigation'
import { useConfig } from '@/services/config'

/**
 * Hook for handling process status changes and appropriate redirections
 * Manages the state machine logic for document signing process
 */
export function useProcessStatusHandler() {
  const [, setLocation] = useLocation()
  const { navigateTo } = useNavigationGuard()
  const { handleSessionExpired } = useConfig()

  const handleProcessStatus = useCallback(
    (status: string | number | undefined) => {
      if (!status) return

      const statusStr = status.toString()

      switch (statusStr) {
        case 'WAITING_FOR_SUBJECT_ACCEPTANCE':
        case ProcessDtoLiteStatus.WAITING_FOR_SUBJECT_ACCEPTANCE.toString():
          // This is the expected status for Summary page - continue normal flow
          break

        case 'AUTHENTICATION_IN_PROGRESS':
        case ProcessDtoLiteStatus.AUTHENTICATION_IN_PROGRESS.toString():
        case 'PREPARING':
        case ProcessDtoLiteStatus.PREPARING.toString():
        case 'SIGNING_IN_PROGRESS':
        case ProcessDtoLiteStatus.SIGNING_IN_PROGRESS.toString():
          if (advanceToStep('waiting-for-signature')) {
            navigateTo('/waiting-for-signature')
          }
          break

        case 'VERIFICATION_FAILED':
        case ProcessDtoLiteStatus.VERIFICATION_FAILED.toString():
          setLocation('/process-error?errorType=verification-failed')
          break

        case 'PROCESS_ERROR':
        case ProcessDtoLiteStatus.PROCESS_ERROR.toString():
          setLocation('/process-error?errorType=process-error')
          break

        case 'EXPIRED':
        case ProcessDtoLiteStatus.EXPIRED.toString():
          handleSessionExpired()
          break

        case 'TERMINATED':
        case ProcessDtoLiteStatus.TERMINATED.toString():
          setLocation('/process-error?errorType=terminated')
          break

        case 'ARCHIVED':
        case ProcessDtoLiteStatus.ARCHIVED.toString():
          setLocation('/process-error?errorType=archived')
          break

        case 'SIGNED':
        case ProcessDtoLiteStatus.SIGNED.toString():
          if (advanceToStep('waiting-for-signature')) {
            navigateTo('/waiting-for-signature')
          }
          break

        default:
          setLocation('/process-error?errorType=invalid-status')
          // eslint-disable-next-line no-console
          console.warn(`Unexpected status in Summary page: ${status}`)
          break
      }
    },
    [setLocation, navigateTo, handleSessionExpired]
  )

  return {
    handleProcessStatus,
  }
}
